import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { 
  Eye, 
  Image, 
  Megaphone, 
  Gift, 
  Tv, 
  Package,
  Coins,
  CreditCard,
  ArrowUpRight,
  Settings,
  Home,
  LayoutDashboard,
} from "lucide-react";
import { UserButton, useUser } from "@clerk/clerk-react";
import { useCredits } from "@/hooks/use-user";
import { Button } from "@/components/ui/button";

const modules = [
  {
    title: "Virtual Try-On",
    path: "/app/try-on",
    icon: Eye,
  },
  {
    title: "Product Staging",
    path: "/app/staging",
    icon: Image,
  },
  {
    title: "Ad Generator",
    path: "/app/ads",
    icon: Megaphone,
  },
  {
    title: "Gift Designer",
    path: "/app/designer",
    icon: Gift,
  },
  {
    title: "AR Visualizer",
    path: "/app/visualizer",
    icon: Tv,
  },
  {
    title: "Unboxing Videos",
    path: "/app/unboxing",
    icon: Package,
  },
];

const Sidebar = () => {
  const location = useLocation();
  const { user } = useUser();
  const { credits, subscriptionTier } = useCredits();

  return (
    <aside className="w-64 bg-background border-r border-yukon-gold flex flex-col h-screen fixed left-0 top-0">
      <div className="p-6">
        <Link to="/" className="text-2xl font-bold text-primary">
          MerchMidas
        </Link>
      </div>

      {/* Credits Display */}
      <div className="px-4 pb-4">
        <Link 
          to="/app/billing" 
          className="block bg-sidebar-accent/50 rounded-lg p-3 hover:bg-sidebar-accent transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-primary">
              <Coins className="w-4 h-4" />
              <span className="text-sm font-medium">{credits} Credits</span>
            </div>
            <CreditCard className="w-4 h-4 text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground mt-1 capitalize">
            {subscriptionTier} Plan
          </p>
        </Link>
        {subscriptionTier === "free" && (
          <Button
            asChild
            size="sm"
            className="w-full mt-2 gap-2"
          >
            <Link to="/pricing">
              <ArrowUpRight className="w-3 h-3" />
              Upgrade
            </Link>
          </Button>
        )}
      </div>

      <nav className="flex-1 px-4 space-y-1">
        {/* Dashboard Home */}
        <Link
          to="/app"
          className={cn(
            "flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200",
            location.pathname === "/app"
              ? "text-primary bg-sidebar-accent"
              : "text-sidebar-foreground hover:text-primary hover:bg-sidebar-accent/50"
          )}
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="text-sm font-medium">Dashboard</span>
        </Link>

        {/* Divider */}
        <div className="h-px bg-border my-2" />

        {modules.map((module) => {
          const isActive = location.pathname === module.path;
          const Icon = module.icon;
          
          return (
            <Link
              key={module.path}
              to={module.path}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200",
                isActive 
                  ? "text-primary bg-sidebar-accent" 
                  : "text-sidebar-foreground hover:text-primary hover:bg-sidebar-accent/50"
              )}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm font-medium">{module.title}</span>
            </Link>
          );
        })}

        {/* Divider */}
        <div className="h-px bg-border my-3" />

        {/* Billing Link */}
        <Link
          to="/app/billing"
          className={cn(
            "flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200",
            location.pathname === "/app/billing"
              ? "text-primary bg-sidebar-accent"
              : "text-sidebar-foreground hover:text-primary hover:bg-sidebar-accent/50"
          )}
        >
          <Settings className="w-5 h-5" />
          <span className="text-sm font-medium">Billing</span>
        </Link>
      </nav>

      <div className="p-4 border-t border-yukon-gold space-y-3">
        {/* Back to Home */}
        <Link
          to="/"
          className="flex items-center gap-3 px-4 py-2.5 rounded-md text-muted-foreground hover:text-primary hover:bg-sidebar-accent/50 transition-colors text-sm font-medium w-full"
        >
          <Home className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 px-2">
          <UserButton afterSignOutUrl="/" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.firstName || user?.emailAddresses[0]?.emailAddress?.split('@')[0] || 'User'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.emailAddresses[0]?.emailAddress || ''}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
